import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { Box, Avatar, Typography, Paper } from "@mui/material";
import { motion } from "framer-motion";
import {
  FaStar,
  FaUserCircle,
  FaQuoteLeft,
  FaCalendarAlt,
} from "react-icons/fa";
import {
  getCourseReviews,
  getInstructorReviews,
} from "../../State/Review/Action";
import AppPagination from "../Tools/AppPagination";

const CourseReviews = ({ courseId }) => {
  const dispatch = useDispatch();
  const [page, setPage] = useState(0);

  const reviewsData = useSelector((state) => state.review?.courseReviews?.data);

  const reviews = reviewsData?.reviews || [];
  const totalPages = reviewsData?.totalPages || 0;
  const totalReviews = reviewsData?.totalReviews || 0;

  useEffect(() => {
    if (courseId) {
      dispatch(getCourseReviews(courseId, page));
    }
  }, [dispatch, courseId, page]);

  const formatDate = (isoDate) => {
    const options = { year: "numeric", month: "long", day: "numeric" };
    return new Date(isoDate).toLocaleDateString(undefined, options);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-blue-50 to-indigo-100 py-10 px-4">
      <div className="max-w-4xl mx-auto">
        <Typography
          variant="h4"
          className="text-indigo-700 font-bold text-center mb-8 flex items-center justify-center gap-2"
        >
          <FaStar className="text-yellow-500" /> Learner Reviews ({totalReviews}
          )
        </Typography>

        <div className="space-y-4">
          {reviews.map((review, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <Paper
                elevation={3}
                className="p-4 rounded-xl bg-white shadow-md flex items-center gap-4 relative"
              >
                <div className="flex flex-col items-center justify-center">
                  <Avatar
                    src={review.learnerProfileImageUrl}
                    alt={review.learnerName}
                    sx={{ width: 56, height: 56 }}
                  />
                </div>
                <div className="flex-1">
                  <div className="flex justify-between items-center mb-1">
                    <Typography
                      variant="h6"
                      className="text-indigo-800 font-semibold"
                    >
                      <FaUserCircle className="inline-block mr-1 text-blue-500" />
                      {review.learnerName}
                    </Typography>
                    <div className="flex items-center gap-2">
                      <div className="flex text-yellow-500 text-sm">
                        {Array.from({ length: review.rating }).map((_, i) => (
                          <FaStar key={i} className="inline-block mr-0.5" />
                        ))}
                      </div>
                      <Typography
                        variant="body2"
                        className="text-gray-500 text-sm"
                      >
                        <FaCalendarAlt className="inline-block mr-1" />
                        {formatDate(review.reviewedAt)}
                      </Typography>
                    </div>
                  </div>
                  <Typography
                    variant="body1"
                    className="text-gray-800 italic flex items-start gap-2"
                  >
                    <FaQuoteLeft className="text-indigo-400 mt-1" />
                    {review.reviewText}
                  </Typography>
                </div>
              </Paper>
            </motion.div>
          ))}
        </div>

        {totalPages > 1 && (
          <Box display="flex" justifyContent="center" mt={4}>
            <AppPagination setPage={setPage} totalPages={totalPages} />
          </Box>
        )}
      </div>
    </div>
  );
};

export default CourseReviews;
